import { useEffect, useMemo, useRef, useState } from "react";
import {
  Landmark, Plus, Pencil, Trash2, Loader2, AlertCircle, CheckCircle2,
  X, Search, ChevronDown, ChevronRight, UserPlus, CalendarDays, LogOut, Users,
} from "lucide-react";

import PageGate from "../../../components/PageGate";
import AccessGate from "../../../components/AccessGate";
import { PERMS } from "../../../utils/perms";
import { describeApiError } from "../../../lib/apiError";
import { useLazySearchMembersByQQuery } from "../../application/api/probationerApplicationApi";
import {
  useGetNationalRegisterQuery,
  useCreateNationalPositionMutation,
  useUpdateNationalPositionMutation,
  useDeleteNationalPositionMutation,
  useAssignNationalHolderMutation,
  useUpdateNationalHolderMutation,
  useEndNationalHolderMutation,
  useRemoveNationalHolderMutation,
  isPermanentGroup,
  isPermanentPosition,
  type NationalGroupKey,
  type NationalPosition,
  type NationalHolder,
} from "../api/nationalBodyApi";

type UiMsg = { type: "success" | "error"; text: string } | null;

/** The register's headings, in the order the secretariat keeps them. */
const GROUP_ORDER: NationalGroupKey[] = [
  "past_president", "past_secretary", "bot", "national_body", "qsrbn_rep", "other",
];

const GROUP_LABEL: Record<NationalGroupKey, string> = {
  past_president: "Past Presidents",
  past_secretary: "Past Secretaries",
  bot: "Board of Trustees",
  national_body: "National Bodies",
  qsrbn_rep: "QSRBN & External Representatives",
  other: "Other",
};

// ── Small helpers ────────────────────────────────────────────────────────────

function holderName(h: NationalHolder) {
  const m = h.member;
  if (!m) return "Unknown member";
  return [m.title, m.surname, m.firstname, m.othernames].filter(Boolean).join(" ");
}

function memberNumber(h: NationalHolder) {
  return h.member?.membership_no || h.member?.member_member_no || "";
}

function initials(name: string) {
  const parts = name.replace(/[^A-Za-z ]/g, " ").trim().split(/\s+/);
  return ((parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? "")).toUpperCase() || "?";
}

/** "12 Jan 2020" — plain enough to read at a glance. */
function prettyDate(d?: string | null) {
  if (!d) return null;
  const parsed = new Date(`${String(d).slice(0, 10)}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return String(d).slice(0, 10);
  return parsed.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
}

/**
 * The tenure in words, which is what an admin actually wants to read.
 *
 * On a seat held for life the dates mean something else entirely: they are the
 * term the person served in the substantive office, not a tenure that is
 * running or finished. "Serving since 2016" under a past president is simply
 * wrong — he served THEN, and he is a past president now and for good.
 */
function tenureText(h: NationalHolder, permanent = false) {
  const from = prettyDate(h.started_at);
  const to = prettyDate(h.ended_at);

  if (permanent) {
    if (from && to) return `Served ${from} – ${to}`;
    if (from) return `Served from ${from}`;
    if (to) return `Served until ${to}`;
    return "On the register";
  }

  if (h.is_current) return from ? `Serving since ${from}` : "Currently serving";
  if (from && to) return `${from} – ${to}`;
  if (to) return `Until ${to}`;
  return "No longer serving";
}

/** The one-line note a permanent group carries wherever it is listed. */
const PERMANENT_NOTE = "Held for life — these seats are never closed.";

function searchResultName(m: any) {
  return [m?.surname, m?.other_names ?? m?.firstname, m?.othernames].filter(Boolean).join(" ");
}

const inputCls =
  "w-full border-2 border-gray-100 rounded-xl px-4 py-2.5 text-sm font-medium outline-none focus:border-gray-900 transition-colors";
const labelCls = "text-[11px] font-bold uppercase tracking-wider text-gray-400 block mb-1.5";

// ── Edit dates ───────────────────────────────────────────────────────────────

/**
 * The dates are the record AND the switch: a holder with no end date is
 * serving, one with an end date is not. The modal says so in words, because
 * clearing the field quietly gives somebody their role back.
 */
function EditDatesModal({
  holder,
  positionName,
  permanent = false,
  onClose,
  onDone,
}: {
  holder: NationalHolder;
  positionName: string;
  /** A seat held for life: the dates are a record, not a switch. */
  permanent?: boolean;
  onClose: () => void;
  onDone: (m: UiMsg) => void;
}) {
  const [save, { isLoading }] = useUpdateNationalHolderMutation();
  const [startedAt, setStartedAt] = useState(holder.started_at?.slice(0, 10) ?? "");
  const [endedAt, setEndedAt] = useState(holder.ended_at?.slice(0, 10) ?? "");

  const willBeCurrent = permanent || !endedAt;
  const changesStatus = !permanent && willBeCurrent !== holder.is_current;

  const submit = async () => {
    if (startedAt && endedAt && endedAt < startedAt) {
      return onDone({ type: "error", text: "The end date cannot be before the start date." });
    }

    try {
      const res: any = await save({
        holderId: holder.id,
        started_at: startedAt || null,
        ended_at: endedAt || null,
      }).unwrap();
      onDone({ type: "success", text: res?.message || "Dates updated." });
      onClose();
    } catch (err) {
      onDone({ type: "error", text: describeApiError(err, "Could not update those dates.") });
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center p-0 sm:p-6">
      <div className="bg-white w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl p-6 space-y-5 max-h-[92vh] overflow-y-auto">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="text-lg font-black text-gray-900 leading-tight">Service dates</h3>
            <p className="text-xs text-gray-500 font-medium mt-1">
              {holderName(holder)} — {positionName}
            </p>
          </div>
          <button onClick={onClose} className="p-2 rounded-xl hover:bg-gray-100 shrink-0">
            <X className="w-4 h-4 text-gray-500" />
          </button>
        </div>

        {permanent && (
          <div className="rounded-xl border border-gray-100 bg-gray-50 p-3 text-[12px] font-semibold text-gray-600 leading-relaxed">
            {PERMANENT_NOTE} These dates are the record of the term they served —
            filling them in does not take anybody off the register.
          </div>
        )}

        <div>
          <label className={labelCls}>Served from</label>
          <input type="date" className={inputCls} value={startedAt} onChange={(e) => setStartedAt(e.target.value)} />
          <p className="text-[11px] text-gray-400 font-medium mt-1">
            Leave blank if the start date isn't known.
          </p>
        </div>

        <div>
          <label className={labelCls}>Served until</label>
          <div className="flex items-center gap-2">
            <input type="date" className={inputCls} value={endedAt} onChange={(e) => setEndedAt(e.target.value)} />
            {endedAt && (
              <button
                onClick={() => setEndedAt("")}
                className="px-3 py-2.5 text-[11px] font-bold uppercase tracking-wider text-gray-500 hover:text-gray-900 border-2 border-gray-100 rounded-xl whitespace-nowrap"
              >
                Clear
              </button>
            )}
          </div>
          <p className="text-[11px] text-gray-400 font-medium mt-1">
            {permanent
              ? "When their term in the office ended. Leave blank if it isn't known."
              : "Empty means they are still serving."}
          </p>
        </div>

        {changesStatus && (
          <div className="rounded-xl border border-amber-100 bg-amber-50 p-3 text-[12px] font-semibold text-amber-800 leading-relaxed">
            {willBeCurrent
              ? "Clearing the end date puts them back in the position and gives the role back."
              : "Adding an end date closes the tenure and withdraws the role."}
            {" "}Their grade stays exactly as it is.
          </div>
        )}

        <div className="flex items-center justify-end gap-2 pt-1">
          <button onClick={onClose} className="px-4 py-2.5 text-xs font-black uppercase tracking-wider text-gray-500 hover:text-gray-900">
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={isLoading}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-gray-900 hover:bg-black text-white text-xs font-black uppercase tracking-wider rounded-xl disabled:opacity-40"
          >
            {isLoading && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            Save dates
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Assign panel ─────────────────────────────────────────────────────────────

function AssignHolder({
  position,
  onDone,
}: {
  position: NationalPosition;
  onDone: (msg: UiMsg) => void;
}) {
  const [assign, { isLoading }] = useAssignNationalHolderMutation();
  const [query, setQuery] = useState("");
  const [picked, setPicked] = useState<any | null>(null);
  const [byNumber, setByNumber] = useState("");
  const [startedAt, setStartedAt] = useState("");

  const [triggerSearch, searchState] = useLazySearchMembersByQQuery();
  const results = useMemo(() => ((searchState.data as any)?.data || []) as any[], [searchState.data]);

  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => {
    if (query.trim().length < 2) return;
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => triggerSearch(query.trim()), 350);
    return () => clearTimeout(debounceRef.current);
  }, [query, triggerSearch]);

  const submit = async () => {
    if (!picked && !byNumber.trim()) {
      return onDone({ type: "error", text: "Find the member first, or type their membership number." });
    }

    try {
      const res: any = await assign({
        positionId: position.id,
        member_id: picked ? (picked.member_id ?? picked.id) : undefined,
        membership_no: picked ? undefined : byNumber.trim(),
        started_at: startedAt || undefined,
      }).unwrap();

      setPicked(null);
      setQuery("");
      setByNumber("");
      setStartedAt("");
      onDone({ type: "success", text: res?.message || "Added." });
    } catch (err) {
      onDone({ type: "error", text: describeApiError(err, "Could not add that person.") });
    }
  };

  return (
    <div className="border-2 border-dashed border-gray-100 rounded-2xl p-4 sm:p-5 space-y-4">
      <div className="flex items-center gap-2">
        <UserPlus className="w-4 h-4 text-gray-400" />
        <span className="text-xs font-black uppercase tracking-wider text-gray-500">
          Add someone to {position.name}
        </span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="relative">
          <label className={labelCls}>Find the member</label>
          <input
            value={picked ? searchResultName(picked) : query}
            onChange={(e) => { setPicked(null); setQuery(e.target.value); }}
            className={`${inputCls} pl-9`}
            placeholder="Type a name…"
            disabled={isLoading}
          />
          <Search className="w-3.5 h-3.5 text-gray-400 absolute left-3 top-[38px]" />
          {searchState.isFetching && (
            <Loader2 className="w-3.5 h-3.5 animate-spin text-gray-400 absolute right-3 top-[38px]" />
          )}

          {!picked && query.trim().length >= 2 && !searchState.isFetching && (
            <div className="absolute z-20 left-0 right-0 mt-1 bg-white border-2 border-gray-100 rounded-xl shadow-xl max-h-56 overflow-y-auto">
              {results.length ? (
                results.map((m: any) => (
                  <button
                    key={m.member_id ?? m.id}
                    onClick={() => { setPicked(m); setByNumber(""); }}
                    className="w-full text-left px-4 py-2.5 hover:bg-gray-50 border-b border-gray-50 last:border-0"
                  >
                    <div className="text-sm font-bold text-gray-900">{searchResultName(m)}</div>
                    <div className="text-[11px] text-gray-400 font-medium">
                      {m.membership_no || "no number"} · {m.email || "no email"}
                    </div>
                  </button>
                ))
              ) : (
                <div className="px-4 py-3 text-xs text-gray-400 font-medium">
                  Nobody matched that. Try a surname, or use the membership number.
                </div>
              )}
            </div>
          )}
        </div>

        <div>
          <label className={labelCls}>…or membership number</label>
          <input
            value={byNumber}
            onChange={(e) => { setByNumber(e.target.value); setPicked(null); }}
            className={inputCls}
            placeholder="e.g. 1118"
            disabled={isLoading || !!picked}
          />
        </div>
      </div>

      <div className="flex flex-wrap items-end gap-4">
        <div>
          <label className={labelCls}>Served from (optional)</label>
          <input
            type="date"
            value={startedAt}
            onChange={(e) => setStartedAt(e.target.value)}
            className="border-2 border-gray-100 rounded-xl px-3 py-2.5 text-sm font-medium outline-none focus:border-gray-900"
          />
        </div>

        <button
          onClick={submit}
          disabled={isLoading}
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-gray-900 hover:bg-black text-white text-xs font-black uppercase tracking-wider rounded-xl disabled:opacity-40"
        >
          {isLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
          Add to position
        </button>
      </div>

      <p className="text-[11px] text-gray-400 font-medium leading-relaxed">
        They get this position's role so the portal opens the right screens for them.
        Their grade — member, fellow, probationer — is not changed.
      </p>
    </div>
  );
}

// ── Holder row ───────────────────────────────────────────────────────────────

function HolderRow({
  holder,
  positionName,
  permanent = false,
  onMessage,
  onEditDates,
}: {
  holder: NationalHolder;
  positionName: string;
  /** A seat held for life — no End button, and never shown as "past". */
  permanent?: boolean;
  onMessage: (m: UiMsg) => void;
  onEditDates: (h: NationalHolder) => void;
}) {
  const [endHolder, { isLoading: ending }] = useEndNationalHolderMutation();
  const [removeHolder, { isLoading: removing }] = useRemoveNationalHolderMutation();

  const name = holderName(holder);
  const number = memberNumber(holder);

  /* A permanent seat is always current, even on a row saved before the
     register knew that — so the row reads as itself rather than as "past"
     while the repair migration is still waiting on a deploy. */
  const standing = permanent || holder.is_current;

  const onEnd = async () => {
    if (!window.confirm(
      `End ${name}'s time as ${positionName}?\n\nThey stay on the record as a past holder, and the role is withdrawn. Their grade is not affected.`
    )) return;
    try {
      const res: any = await endHolder({ holderId: holder.id }).unwrap();
      onMessage({ type: "success", text: res?.message || "Tenure ended." });
    } catch (err) {
      onMessage({ type: "error", text: describeApiError(err, "Could not end that tenure.") });
    }
  };

  const onRemove = async () => {
    if (!window.confirm(
      `Remove ${name} from ${positionName} completely?\n\nUse this only if the record was added by mistake — it deletes it rather than keeping them as a past holder.`
    )) return;
    try {
      const res: any = await removeHolder(holder.id).unwrap();
      onMessage({ type: "success", text: res?.message || "Record removed." });
    } catch (err) {
      onMessage({ type: "error", text: describeApiError(err, "Could not remove that record.") });
    }
  };

  return (
    <div
      className={`flex flex-wrap items-center justify-between gap-3 rounded-2xl border px-4 py-3 ${
        standing ? "bg-white border-gray-100" : "bg-gray-50/70 border-gray-100"
      }`}
    >
      <div className="flex items-center gap-3 min-w-0">
        <div
          className={`w-9 h-9 rounded-xl flex items-center justify-center text-[11px] font-black shrink-0 ${
            standing ? "bg-gray-900 text-white" : "bg-gray-200 text-gray-500"
          }`}
        >
          {initials(name)}
        </div>
        <div className="min-w-0">
          <div className="text-sm font-bold text-gray-900 truncate">
            {name}
            {!standing && (
              <span className="ml-2 text-[10px] font-black uppercase tracking-wider text-gray-400">past</span>
            )}
          </div>
          <div className="text-[11px] text-gray-400 font-medium truncate">
            {number ? `${number} · ` : ""}{tenureText(holder, permanent)}
          </div>
        </div>
      </div>

      <AccessGate perm={PERMS.nationalBody.manage}>
        <div className="flex items-center gap-1.5 shrink-0">
          <button
            onClick={() => onEditDates(holder)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border-2 border-gray-100 text-[11px] font-black uppercase tracking-wider text-gray-500 hover:text-gray-900 hover:border-gray-200"
          >
            <CalendarDays className="w-3 h-3" />
            Dates
          </button>

          {/* No End on a seat held for life — the API refuses it too, so a
              button for it would only ever produce an error. Removing the
              record is still there for one added in error. */}
          {!permanent && holder.is_current && (
            <button
              onClick={onEnd}
              disabled={ending}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border-2 border-gray-100 text-[11px] font-black uppercase tracking-wider text-gray-500 hover:text-gray-900 hover:border-gray-200 disabled:opacity-40"
              title="Keeps them on the record as a past holder"
            >
              <LogOut className="w-3 h-3" />
              End
            </button>
          )}

          <button
            onClick={onRemove}
            disabled={removing}
            className="p-1.5 rounded-lg text-red-400 hover:text-red-600 hover:bg-red-50 disabled:opacity-40"
            title="Delete this record — only if it was added by mistake"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </AccessGate>
    </div>
  );
}

// ── Position card ────────────────────────────────────────────────────────────

function PositionCard({
  position,
  expanded,
  onToggle,
  onEdit,
  onMessage,
  onEditDates,
}: {
  position: NationalPosition;
  expanded: boolean;
  onToggle: () => void;
  onEdit: (p: NationalPosition) => void;
  onMessage: (m: UiMsg) => void;
  onEditDates: (h: NationalHolder, positionName: string, permanent: boolean) => void;
}) {
  const [removePosition, { isLoading: deleting }] = useDeleteNationalPositionMutation();

  const holders = position.holders ?? [];

  /* On a seat held for life nobody is "past" — everybody recorded in it holds
     it still, whatever an old row's is_current says. */
  const permanent = isPermanentPosition(position);
  const current = permanent ? holders : holders.filter((h) => h.is_current);
  const past = permanent ? [] : holders.filter((h) => !h.is_current);

  const onDeletePosition = async () => {
    if (!window.confirm(`Delete the position "${position.name}"?`)) return;
    try {
      await removePosition(position.id).unwrap();
      onMessage({ type: "success", text: "Position deleted." });
    } catch (err) {
      onMessage({ type: "error", text: describeApiError(err, "Could not delete that position.") });
    }
  };

  return (
    <div className="bg-white border rounded-2xl shadow-sm overflow-hidden">
      <div
        className="flex items-center justify-between gap-3 px-4 sm:px-5 py-4 cursor-pointer hover:bg-gray-50/60 transition-colors"
        onClick={onToggle}
      >
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-black text-gray-900 text-sm">{position.name}</span>
            {!position.is_active && (
              <span className="px-2 py-0.5 rounded-full text-[10px] font-black bg-gray-100 text-gray-500 uppercase tracking-wider">
                Inactive
              </span>
            )}
          </div>

          {/* Who holds it, without having to open the card. */}
          <div className="text-xs text-gray-500 font-medium mt-1 truncate">
            {current.length === 0 ? (
              <span className="text-amber-600 font-bold">
                {permanent
                  ? "Nobody has been recorded in this position"
                  : "Nobody is serving in this position"}
              </span>
            ) : (
              current.map(holderName).join(", ")
            )}
          </div>

          {past.length > 0 && (
            <div className="text-[11px] text-gray-400 font-medium mt-0.5">
              {past.length} past holder{past.length === 1 ? "" : "s"}
            </div>
          )}
        </div>

        <div className="flex items-center gap-1.5 shrink-0">
          {/* "Vacant" is meaningless on a seat held for life — nothing is
              waiting to be filled, there is simply nobody on record yet. */}
          <span
            className={`px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider ${
              current.length ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"
            }`}
          >
            {current.length
              ? permanent
                ? `${current.length} on record`
                : `${current.length} serving`
              : permanent
                ? "None recorded"
                : "Vacant"}
          </span>

          <AccessGate perm={PERMS.nationalBody.manage}>
            <button
              onClick={(e) => { e.stopPropagation(); onEdit(position); }}
              className="p-1.5 rounded-lg text-gray-400 hover:text-gray-900 hover:bg-gray-100"
              title="Rename or move this position"
            >
              <Pencil className="w-3.5 h-3.5" />
            </button>
            {/* Only an empty position can be deleted — the holder rows are the
                record of who served, and the API refuses the rest. */}
            {holders.length === 0 && (
              <button
                onClick={(e) => { e.stopPropagation(); onDeletePosition(); }}
                disabled={deleting}
                className="p-1.5 rounded-lg text-red-400 hover:text-red-600 hover:bg-red-50 disabled:opacity-40"
                title="Delete this position"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            )}
          </AccessGate>

          {expanded ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
        </div>
      </div>

      {expanded && (
        <div className="border-t px-4 sm:px-5 py-4 space-y-4">
          {position.description && (
            <p className="text-xs text-gray-500 font-medium">{position.description}</p>
          )}

          {holders.length === 0 ? (
            <p className="text-xs text-gray-400 font-medium italic">
              Nobody has been recorded in this position yet.
            </p>
          ) : (
            <div className="space-y-2">
              {[...current, ...past].map((h) => (
                <HolderRow
                  key={h.id}
                  holder={h}
                  positionName={position.name}
                  permanent={permanent}
                  onMessage={onMessage}
                  onEditDates={(holder) => onEditDates(holder, position.name, permanent)}
                />
              ))}
            </div>
          )}

          <AccessGate perm={PERMS.nationalBody.manage}>
            <AssignHolder position={position} onDone={onMessage} />
          </AccessGate>
        </div>
      )}
    </div>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

type PositionForm = {
  id?: number;
  group: NationalGroupKey;
  name: string;
  description: string;
  allows_multiple: boolean;
  rank: string;
  is_active: boolean;
};

const emptyForm: PositionForm = {
  group: "national_body",
  name: "",
  description: "",
  allows_multiple: false,
  rank: "",
  is_active: true,
};

function StatTile({ label, value, tone }: { label: string; value: number; tone?: "amber" }) {
  return (
    <div className={`rounded-2xl border px-4 py-3 ${tone === "amber" ? "bg-amber-50 border-amber-100" : "bg-white"}`}>
      <div className={`text-2xl font-black ${tone === "amber" ? "text-amber-700" : "text-gray-900"}`}>{value}</div>
      <div className="text-[10px] font-black uppercase tracking-wider text-gray-400 mt-0.5">{label}</div>
    </div>
  );
}

function NationalBodiesPageInner() {
  const { data, isLoading, error } = useGetNationalRegisterQuery();
  const [create, { isLoading: creating }] = useCreateNationalPositionMutation();
  const [update, { isLoading: updating }] = useUpdateNationalPositionMutation();

  const [msg, setMsg] = useState<UiMsg>(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<PositionForm>(emptyForm);
  const [search, setSearch] = useState("");
  const [groupFilter, setGroupFilter] = useState<NationalGroupKey | "all">("all");
  const [expanded, setExpanded] = useState<Record<number, boolean>>({});
  const [editing, setEditing] = useState<{
    holder: NationalHolder;
    positionName: string;
    permanent: boolean;
  } | null>(null);

  const positions = useMemo(() => data?.positions ?? [], [data?.positions]);

  const flash = (m: UiMsg) => {
    setMsg(m);
    if (m) setTimeout(() => setMsg(null), 7000);
  };

  const stats = useMemo(() => {
    const serving = positions.reduce(
      (n, p) =>
        n +
        (isPermanentPosition(p)
          ? p.holders?.length ?? 0
          : p.holders?.filter((h) => h.is_current).length ?? 0),
      0,
    );

    /* A seat held for life is never vacant: there is no handover pending, only
       a list the secretariat has not finished loading. Counting it as vacant
       puts an amber number in front of leadership that nobody can act on. */
    const vacant = positions.filter(
      (p) => !isPermanentPosition(p) && !(p.holders ?? []).some((h) => h.is_current),
    ).length;

    return { positions: positions.length, serving, vacant };
  }, [positions]);

  /** Search runs over position names AND the people in them — that's how you look somebody up. */
  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();

    return positions.filter((p) => {
      if (groupFilter !== "all" && p.group !== groupFilter) return false;
      if (!q) return true;

      if (p.name.toLowerCase().includes(q)) return true;
      return (p.holders ?? []).some((h) =>
        holderName(h).toLowerCase().includes(q) || memberNumber(h).toLowerCase().includes(q),
      );
    });
  }, [positions, groupFilter, search]);

  const byGroup = useMemo(() => {
    const map = new Map<NationalGroupKey, NationalPosition[]>();
    visible.forEach((p) => {
      const list = map.get(p.group) ?? [];
      list.push(p);
      map.set(p.group, list);
    });
    return map;
  }, [visible]);

  // A search should show what it found, not make you open every card.
  useEffect(() => {
    if (!search.trim()) return;
    setExpanded((prev) => {
      const next = { ...prev };
      visible.forEach((p) => { next[p.id] = true; });
      return next;
    });
  }, [search, visible]);

  const openCreate = () => { setForm(emptyForm); setShowForm(true); };

  const openEdit = (p: NationalPosition) => {
    setForm({
      id: p.id,
      group: p.group,
      name: p.name,
      description: p.description ?? "",
      allows_multiple: p.allows_multiple,
      rank: p.rank != null ? String(p.rank) : "",
      is_active: p.is_active,
    });
    setShowForm(true);
  };

  const savePosition = async () => {
    if (!form.name.trim()) return flash({ type: "error", text: "Give the position a name." });

    const body = {
      group: form.group,
      name: form.name.trim(),
      description: form.description.trim() || null,
      // Forced on for a lifelong seat, whatever the toggle was left at before
      // the group was changed — the API treats these as multi-holder anyway.
      allows_multiple: form.allows_multiple || isPermanentGroup(form.group),
      rank: form.rank ? Number(form.rank) : null,
      is_active: form.is_active,
    } as any;

    try {
      if (form.id) {
        await update({ id: form.id, ...body }).unwrap();
        flash({ type: "success", text: "Position updated." });
      } else {
        await create(body).unwrap();
        flash({ type: "success", text: "Position created." });
      }
      setShowForm(false);
    } catch (err) {
      flash({ type: "error", text: describeApiError(err, "Could not save that position.") });
    }
  };

  const groupsWithCounts = useMemo(
    () =>
      GROUP_ORDER.map((g) => ({
        key: g,
        label: data?.groups?.[g] ?? GROUP_LABEL[g],
        count: positions.filter((p) => p.group === g).length,
      })).filter((g) => g.count > 0),
    [positions, data?.groups],
  );

  return (
    <div className="space-y-5 max-w-5xl mx-auto pb-20">
      {/* Header */}
      <div className="bg-white border rounded-2xl p-5 sm:p-6 shadow-sm space-y-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-gray-900 rounded-2xl">
              <Landmark className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-black text-gray-900 tracking-tight">
                National Policy Committee
              </h1>
              <p className="text-sm text-gray-500 font-medium max-w-xl">
                Past presidents and secretaries, the Board of Trustees, the national bodies
                and the Institute's representatives on outside boards — and who holds each one.
              </p>
            </div>
          </div>

          <AccessGate perm={PERMS.nationalBody.manage}>
            <button
              onClick={openCreate}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-gray-900 hover:bg-black text-white text-xs font-black uppercase tracking-wider rounded-xl"
            >
              <Plus className="w-3.5 h-3.5" />
              New Position
            </button>
          </AccessGate>
        </div>

        <div className="grid grid-cols-3 gap-3">
          <StatTile label="Positions" value={stats.positions} />
          <StatTile label="People serving" value={stats.serving} />
          <StatTile label="Vacant" value={stats.vacant} tone="amber" />
        </div>

        {/* Search + group filter */}
        <div className="space-y-3">
          <div className="relative">
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className={`${inputCls} pl-10`}
              placeholder="Search a position, a person or a membership number…"
            />
            <Search className="w-4 h-4 text-gray-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
            {search && (
              <button
                onClick={() => setSearch("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-lg hover:bg-gray-100"
              >
                <X className="w-3.5 h-3.5 text-gray-400" />
              </button>
            )}
          </div>

          {groupsWithCounts.length > 1 && (
            <div className="flex items-center gap-2 overflow-x-auto pb-1">
              {[{ key: "all" as const, label: "All", count: positions.length }, ...groupsWithCounts].map((g) => (
                <button
                  key={g.key}
                  onClick={() => setGroupFilter(g.key as any)}
                  className={`px-3.5 py-1.5 rounded-xl text-[11px] font-black uppercase tracking-wider whitespace-nowrap transition-colors ${
                    groupFilter === g.key
                      ? "bg-gray-900 text-white"
                      : "bg-gray-50 text-gray-500 hover:text-gray-900"
                  }`}
                >
                  {g.label} <span className="opacity-60">{g.count}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {msg && (
        <div
          className={`flex items-start gap-3 rounded-2xl border p-4 text-sm font-bold ${
            msg.type === "success"
              ? "bg-green-50 border-green-100 text-green-800"
              : "bg-red-50 border-red-100 text-red-800"
          }`}
        >
          {msg.type === "success" ? <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" /> : <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />}
          <span className="leading-relaxed">{msg.text}</span>
        </div>
      )}

      {isLoading && (
        <div className="py-16 text-center text-sm text-gray-400 font-medium animate-pulse">
          Loading the register…
        </div>
      )}

      {error && (
        <div className="rounded-2xl border border-red-100 bg-red-50 p-5 text-sm font-bold text-red-700">
          {describeApiError(error, "Could not load the register.")}
        </div>
      )}

      {!isLoading && !error && positions.length === 0 && (
        <div className="bg-white border rounded-2xl py-16 text-center px-6">
          <Landmark className="w-10 h-10 text-gray-200 mx-auto mb-3" />
          <p className="text-sm font-bold text-gray-500">No positions yet</p>
          <p className="text-xs text-gray-400 font-medium mt-1">
            Create one with the button above, or run the NationalBodySeeder to load the secretariat's list.
          </p>
        </div>
      )}

      {!isLoading && !error && positions.length > 0 && visible.length === 0 && (
        <div className="bg-white border rounded-2xl py-14 text-center px-6">
          <Users className="w-9 h-9 text-gray-200 mx-auto mb-3" />
          <p className="text-sm font-bold text-gray-500">Nothing matches "{search}"</p>
          <button
            onClick={() => { setSearch(""); setGroupFilter("all"); }}
            className="text-xs font-black uppercase tracking-wider text-indigo-600 mt-2"
          >
            Clear the search
          </button>
        </div>
      )}

      {/* Groups */}
      {GROUP_ORDER.filter((g) => (byGroup.get(g)?.length ?? 0) > 0).map((g) => (
        <div key={g} className="space-y-3">
          <h2 className="text-[11px] font-black uppercase tracking-widest text-gray-400 px-1 flex flex-wrap items-center gap-2">
            {data?.groups?.[g] ?? GROUP_LABEL[g]}
            {isPermanentGroup(g) && (
              <span
                title={PERMANENT_NOTE}
                className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 text-[9px] tracking-wider normal-case font-black"
              >
                Held for life
              </span>
            )}
          </h2>
          <div className="space-y-3">
            {(byGroup.get(g) ?? []).map((p) => (
              <PositionCard
                key={p.id}
                position={p}
                expanded={!!expanded[p.id]}
                onToggle={() => setExpanded((prev) => ({ ...prev, [p.id]: !prev[p.id] }))}
                onEdit={openEdit}
                onMessage={flash}
                onEditDates={(holder, positionName, permanent) =>
                  setEditing({ holder, positionName, permanent })
                }
              />
            ))}
          </div>
        </div>
      ))}

      {/* Service dates */}
      {editing && (
        <EditDatesModal
          holder={editing.holder}
          positionName={editing.positionName}
          permanent={editing.permanent}
          onClose={() => setEditing(null)}
          onDone={flash}
        />
      )}

      {/* Create / edit position */}
      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center p-0 sm:p-6">
          <div className="bg-white w-full sm:max-w-lg rounded-t-3xl sm:rounded-3xl p-6 space-y-4 max-h-[92vh] overflow-y-auto">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-black text-gray-900">
                {form.id ? "Edit position" : "New position"}
              </h3>
              <button onClick={() => setShowForm(false)} className="p-2 rounded-xl hover:bg-gray-100">
                <X className="w-4 h-4 text-gray-500" />
              </button>
            </div>

            <div>
              <label className={labelCls}>Which part of the register</label>
              <select
                className={inputCls}
                value={form.group}
                onChange={(e) => setForm((p) => ({ ...p, group: e.target.value as NationalGroupKey }))}
              >
                {GROUP_ORDER.map((g) => (
                  <option key={g} value={g}>{data?.groups?.[g] ?? GROUP_LABEL[g]}</option>
                ))}
              </select>
            </div>

            <div>
              <label className={labelCls}>Name of the position</label>
              <input
                className={inputCls}
                value={form.name}
                onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                placeholder="e.g. Chairman, Fellows Forum"
              />
            </div>

            <div>
              <label className={labelCls}>Note (optional)</label>
              <input
                className={inputCls}
                value={form.description}
                onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                placeholder="Anything worth saying about this position"
              />
            </div>

            <div>
              <label className={labelCls}>Order within the group (optional)</label>
              <input
                type="number"
                className={inputCls}
                value={form.rank}
                onChange={(e) => setForm((p) => ({ ...p, rank: e.target.value }))}
                placeholder="1, 2, 3…"
              />
            </div>

            {/* A lifelong seat accumulates holders — there is no handover to
                make room — so the choice is not the admin's to make there. */}
            <label className="flex items-center justify-between gap-4 py-2">
              <span className="text-sm font-bold text-gray-700">
                Several people at once
                <span className="block text-[11px] font-medium text-gray-400 mt-0.5">
                  {isPermanentGroup(form.group)
                    ? `${PERMANENT_NOTE} A position in this part of the register always holds several people.`
                    : "On for a trustee or a body with several members. Off for a single office such as Registrar, QSRBN."}
                </span>
              </span>
              <button
                disabled={isPermanentGroup(form.group)}
                onClick={() => setForm((p) => ({ ...p, allows_multiple: !p.allows_multiple }))}
                className={`w-10 h-6 rounded-full transition-colors shrink-0 disabled:opacity-60 ${
                  form.allows_multiple || isPermanentGroup(form.group) ? "bg-indigo-500" : "bg-gray-200"
                }`}
              >
                <span
                  className={`block w-4 h-4 bg-white rounded-full transition-transform ${
                    form.allows_multiple || isPermanentGroup(form.group) ? "translate-x-5" : "translate-x-1"
                  }`}
                />
              </button>
            </label>

            <label className="flex items-center justify-between gap-4 py-2">
              <span className="text-sm font-bold text-gray-700">
                Active
                <span className="block text-[11px] font-medium text-gray-400 mt-0.5">
                  Turn off for a position the Institute no longer fills.
                </span>
              </span>
              <button
                onClick={() => setForm((p) => ({ ...p, is_active: !p.is_active }))}
                className={`w-10 h-6 rounded-full transition-colors shrink-0 ${form.is_active ? "bg-emerald-500" : "bg-gray-200"}`}
              >
                <span className={`block w-4 h-4 bg-white rounded-full transition-transform ${form.is_active ? "translate-x-5" : "translate-x-1"}`} />
              </button>
            </label>

            <div className="flex items-center justify-end gap-2 pt-2">
              <button
                onClick={() => setShowForm(false)}
                className="px-4 py-2.5 text-xs font-black uppercase tracking-wider text-gray-500 hover:text-gray-900"
              >
                Cancel
              </button>
              <button
                onClick={savePosition}
                disabled={creating || updating}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-gray-900 hover:bg-black text-white text-xs font-black uppercase tracking-wider rounded-xl disabled:opacity-40"
              >
                {(creating || updating) && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Reading the register is its own capability; maintaining it (which grants
 * people roles) is another — hence the gate here and the AccessGates inside.
 */
export default function NationalBodiesPage() {
  return (
    <PageGate perm={PERMS.nationalBody.access}>
      <NationalBodiesPageInner />
    </PageGate>
  );
}
